import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Link2, Copy, Check, Search, UserCheck, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';
import PaymentLinkModal from './PaymentLinkModal';

const API_URL = getApiUrl();

const LeadPicker = ({ selected, onSelect, onManual }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setFailed(false); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      setFailed(false);
      try {
        const res = await axios.get(`${API_URL}/api/payments/lead-search`, { params: { q: term }, withCredentials: true });
        setResults(res.data || []);
      } catch { setResults([]); setFailed(true); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  if (selected) {
    return (
      <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50" data-testid="quick-pay-selected-lead">
        <div className="flex items-start gap-2 min-w-0">
          <UserCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
          <div className="min-w-0 text-sm">
            <div className="font-semibold text-slate-900 truncate" data-testid="quick-pay-selected-lead-name">{selected.full_name}</div>
            <div className="text-slate-600 truncate">{selected.phone || '—'} · {selected.email || 'no email'}</div>
            <div className="text-xs text-slate-500 mt-0.5">Payment will be mapped to this lead on capture, even if the customer pays with a different number or email.</div>
          </div>
        </div>
        <button type="button" onClick={() => onSelect(null)} className="text-xs font-medium text-slate-600 hover:text-slate-900 shrink-0" data-testid="quick-pay-change-lead-btn">Change</button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search lead by name, phone or email…"
          className="input-base pl-9"
          data-testid="quick-pay-lead-search"
          autoFocus
        />
      </div>
      {(results.length > 0 || searching || q.trim().length >= 2) && (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto bg-white" data-testid="quick-pay-lead-results">
          {searching && results.length === 0 && <div className="p-3 text-sm text-slate-500">Searching…</div>}
          {!searching && failed && (
            <div className="p-3 text-sm text-rose-600" data-testid="quick-pay-lead-search-error">Search failed — check your connection and try again.</div>
          )}
          {!searching && !failed && results.length === 0 && q.trim().length >= 2 && (
            <div className="p-3 text-sm text-slate-500" data-testid="quick-pay-lead-no-results">No lead matches “{q.trim()}”.</div>
          )}
          {results.map((l) => (
            <button
              type="button"
              key={l.lead_id}
              onClick={() => onSelect(l)}
              className="w-full text-left p-3 hover:bg-slate-50 transition-colors"
              data-testid={`quick-pay-lead-option-${l.lead_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-900 truncate">{l.full_name}{l.is_test && <span className="ml-1.5 text-[10px] font-semibold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">TEST</span>}</span>
                <span className="text-[11px] uppercase tracking-wide text-slate-500 shrink-0">{l.enrollment_status === 'enrolled' ? 'Enrolled' : l.trial_payment_status === 'paid' ? 'Trial paid' : l.lead_status || 'lead'}</span>
              </div>
              <div className="text-xs text-slate-500 truncate">{l.phone || '—'} · {l.email || 'no email'}{l.country ? ` · ${l.country}` : ''}</div>
            </button>
          ))}
        </div>
      )}
      <button type="button" onClick={onManual} className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800" data-testid="quick-pay-manual-toggle">
        <UserPlus className="w-3.5 h-3.5" /> Lead not found? Enter customer details manually
      </button>
    </div>
  );
};

const QuickPaymentLinkModal = ({ pricingConfigs = [], onClose, onSuccess }) => {
  const [currency, setCurrency] = useState('INR');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [manual, setManual] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [expectedDueDate, setExpectedDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  // V22 — Once a lead is picked, hand off to the full-featured payment modal so
  // course type, tenure and pricing auto-populate exactly like the lead-row
  // credit-card flow (fixes quick-link defaulting every capture to 1 month).
  if (selectedLead) {
    return (
      <PaymentLinkModal
        lead={selectedLead}
        pricingConfigs={pricingConfigs}
        onClose={() => setSelectedLead(null)}
        onSuccess={() => { onSuccess?.(); onClose(); }}
      />
    );
  }

  const handleGenerate = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!selectedLead && !manual) { toast.error('Select a lead first (or enter customer details manually)'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/payments/quick-link`, {
        currency,
        amount: amt,
        description: description.trim() || undefined,
        lead_id: selectedLead?.lead_id || undefined,
        customer_name: !selectedLead ? (customerName.trim() || undefined) : undefined,
        customer_email: !selectedLead ? (customerEmail.trim() || undefined) : undefined,
        customer_phone: !selectedLead ? (customerPhone.trim() || undefined) : undefined,
        expected_due_date: expectedDueDate || undefined,
      }, { withCredentials: true });
      setGeneratedLink(res.data);
      toast.success('Payment link generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!generatedLink?.short_url) return;
    try {
      await navigator.clipboard.writeText(generatedLink.short_url);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const shareWhatsApp = () => {
    if (!generatedLink?.short_url) return;
    const sym = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : currency;
    const text = `Hi! Here's your payment link for Soul Sanskar (${sym}${parseFloat(amount).toLocaleString()}):\n${generatedLink.short_url}`;
    const phone = (selectedLead?.phone || customerPhone || '').replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const resetForm = () => {
    setGeneratedLink(null); setAmount(''); setDescription(''); setSelectedLead(null); setManual(false);
    setCustomerName(''); setCustomerEmail(''); setCustomerPhone(''); setExpectedDueDate('');
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="quick-payment-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-slate-900">Generate Payment Link</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="quick-pay-close-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5">
          {!generatedLink ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Lead *</label>
                {manual && !selectedLead ? (
                  <div className="space-y-2" data-testid="quick-pay-manual-fields">
                    <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" className="input-base" data-testid="quick-pay-customer-name" />
                    <div className="grid grid-cols-2 gap-2">
                      <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="Email" className="input-base" data-testid="quick-pay-customer-email" />
                      <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Phone (e.g. +919876543210)" className="input-base" data-testid="quick-pay-customer-phone" />
                    </div>
                    <button type="button" onClick={() => setManual(false)} className="text-xs font-medium text-indigo-600 hover:text-indigo-800" data-testid="quick-pay-back-to-search">← Back to lead search</button>
                  </div>
                ) : (
                  <LeadPicker selected={selectedLead} onSelect={setSelectedLead} onManual={() => setManual(true)} />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Currency *</label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="input-base" data-testid="quick-pay-currency">
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="CAD">CAD (C$)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                  <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 99" className="input-base" data-testid="quick-pay-amount" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Trial Class, Consultation" className="input-base" data-testid="quick-pay-description" />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Expected Due Date (optional — fills the lead's EDD on payment & syncs to LMS)</label>
                <input type="date" value={expectedDueDate} onChange={(e) => setExpectedDueDate(e.target.value)} className="input-base" data-testid="quick-pay-edd" />
              </div>
              <p className="text-xs text-slate-500">After paying, the customer is taken straight to the learner portal and logged in (OTP verified).</p>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={onClose} className="btn-secondary">Cancel</button>
                <button onClick={handleGenerate} disabled={loading} className="btn-primary" data-testid="quick-pay-generate-btn">
                  {loading ? 'Generating...' : 'Generate Link'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center mb-2">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900">Link Ready!</h3>
                <p className="text-sm text-slate-500">Share this link with {selectedLead?.full_name || customerName || 'the customer'}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <input type="text" readOnly value={generatedLink.short_url} className="input-base flex-1 bg-white text-sm" data-testid="quick-pay-link" />
                  <button onClick={copyLink} className={`p-2 rounded-md transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`} data-testid="quick-pay-copy-btn">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-medium">{currency} {parseFloat(amount).toLocaleString()}</span></div>
                {(selectedLead || generatedLink.lead_id) && <div className="flex justify-between"><span className="text-slate-500">Mapped lead</span><span className="font-medium truncate max-w-[60%]" data-testid="quick-pay-mapped-lead">{selectedLead?.full_name || generatedLink.lead_id}</span></div>}
                {description && <div className="flex justify-between"><span className="text-slate-500">Description</span><span className="font-medium">{description}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Expires</span><span className="font-medium">7 days</span></div>
              </div>
              <div className="flex gap-3">
                <button onClick={shareWhatsApp} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm" data-testid="quick-pay-whatsapp-btn">
                  Share on WhatsApp
                </button>
                <button onClick={resetForm} className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid="quick-pay-new-link-btn">
                  New Link
                </button>
              </div>
              <button onClick={() => { onSuccess?.(); onClose(); }} className="w-full btn-secondary text-sm" data-testid="quick-pay-done-btn">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuickPaymentLinkModal;
